// src/features/system/archiver/components/ArchiverDashboard.tsx
// ────────────────────────────────────────────────────────────────────────────
// Archiver — Zero-Prop Dashboard.
//
// CONTAINER component: owns data via useArchiver(), delegates rendering to
// the shared ArchiveStatusView presenter. Adds dashboard-specific controls
// (header, refresh, trigger, granularity tabs, summary table).
//
// The ArchiveStatusView is the SINGLE SOURCE OF TRUTH for archive status
// rendering — shared between this dashboard and the agent-chat ArchiverCard.
// ────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import DarkDataTable, { type DarkDataTableColumn } from '@/shared/components/ui/DarkDataTable';
import { StatusIndicator, humanBytes, fmtNum } from '@/shared/components/dashboard';
import ArchiveStatusView from './ArchiveStatusView';
import { useArchiver } from '../hooks/useArchiver';
import type { ArchiveSummaryRow } from '../api/archiverApi';

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(ms: number): string {
  if (!ms) return '—';
  const sec = Math.floor((Date.now() - ms) / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}

// ── Granularity Tab Strip ────────────────────────────────────────────────────

const GRANULARITIES = ['daily', 'weekly', 'monthly'] as const;

function GranularityTabs({ active, onChange }: {
  active: string;
  onChange: (g: 'daily' | 'weekly' | 'monthly') => void;
}) {
  return (
    <div className="flex gap-1">
      {GRANULARITIES.map(g => (
        <button
          key={g}
          onClick={() => onChange(g)}
          className={`pac-glass-tab px-3 py-1 text-xs font-mono uppercase tracking-wide transition-colors ${
            active === g ? 'pac-tab-active text-emerald-400 border-b-emerald-400' : 'text-neutral-500 hover:text-neutral-300'
          }`}
        >
          {g}
        </button>
      ))}
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────────────────

export default function ArchiverDashboard() {
  const {
    status, statusLoading, statusUpdatedAt,
    namespaceBreakdown, partitionTimeline,
    granularity, setGranularity,
    triggerArchive, triggerPending,
    refresh,
    summary, summaryLoading,
  } = useArchiver();

  // ── Summary table columns ─────────────────────────────────────────────
  const summaryRows = summary?.summary ?? [];
  const summaryColumns = useMemo<DarkDataTableColumn<ArchiveSummaryRow>[]>(() => [
    {
      header: 'Namespace',
      render: (r) => (
        <span className="font-mono text-[11px] text-emerald-400">{r.scope_ns}</span>
      ),
    },
    {
      header: 'Period',
      render: (r) => (
        <span className="text-neutral-400 text-[11px]">
          {r.period_start}{r.period_end !== r.period_start ? ` → ${r.period_end}` : ''}
        </span>
      ),
    },
    {
      header: 'Rows',
      render: (r) => <span className="tabular-nums text-neutral-200">{fmtNum(r.total_rows)}</span>,
      thClassName: 'text-right',
      tdClassName: 'text-right',
    },
    {
      header: 'Keys',
      render: (r) => <span className="tabular-nums text-neutral-400">{fmtNum(r.distinct_keys)}</span>,
      thClassName: 'text-right',
      tdClassName: 'text-right',
    },
    {
      header: 'Files',
      render: (r) => <span className="tabular-nums text-violet-400">{fmtNum(r.parquet_files)}</span>,
      thClassName: 'text-right',
      tdClassName: 'text-right',
    },
    {
      header: 'Size',
      render: (r) => <span className="tabular-nums text-neutral-300 text-[11px]">{humanBytes(r.parquet_bytes)}</span>,
      thClassName: 'text-right',
      tdClassName: 'text-right',
    },
  ], []);

  // ── Loading skeleton ───────────────────────────────────────────────────
  if (statusLoading) {
    return (
      <div className="p-6 space-y-4 max-w-7xl mx-auto animate-pulse">
        <div className="h-8 w-48 bg-white/[0.04] rounded" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="pac-glass rounded-lg border border-white/[0.06] p-4 h-24" />
          ))}
        </div>
        <div className="pac-glass rounded-lg border border-white/[0.06] h-64" />
      </div>
    );
  }

  const archive = status?.archive;
  const pipeline = status?.pipeline;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold tracking-tight text-neutral-100">
            Archive Pipeline
          </h2>
          <StatusIndicator
            active={!!archive && archive.total_archived_rows > 0}
            label={archive && archive.total_archived_rows > 0 ? 'Pipeline Active' : 'Pipeline Disabled'}
            sub={pipeline?.retention_days ? `${pipeline.retention_days}d retention` : undefined}
          />
        </div>
        <div className="flex items-center gap-3">
          {statusUpdatedAt > 0 && (
            <span className="text-[10px] text-neutral-600 font-mono">
              {timeAgo(statusUpdatedAt)}
            </span>
          )}
          <button
            onClick={() => refresh()}
            className="pac-glass-btn px-3 py-1.5 text-xs text-neutral-400 rounded hover:text-neutral-200"
          >
            Refresh
          </button>
          <button
            onClick={() => triggerArchive()}
            disabled={triggerPending}
            className="pac-glass-btn px-3 py-1.5 text-xs text-emerald-400 rounded hover:text-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {triggerPending ? (
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Archiving…
              </span>
            ) : (
              'Trigger Archive'
            )}
          </button>
        </div>
      </div>

      {/* ── Shared Archive Status Presenter ────────────────────────── */}
      <ArchiveStatusView
        db={status?.db}
        archive={archive}
        savings={status?.savings}
        pipeline={pipeline}
        namespaceBreakdown={namespaceBreakdown}
        partitions={partitionTimeline.map(p => ({
          name: p.name,
          date: p.date,
          rows: p.rows,
          size: p.size,
        }))}
        summaryRows={summaryRows.map(r => ({
          scope_ns: r.scope_ns,
          period_start: r.period_start,
          period_end: r.period_end,
          total_rows: r.total_rows,
          parquet_files: r.parquet_files,
          parquet_bytes: r.parquet_bytes,
          created_at: '',
        }))}
      />

      {/* ── Archive Summary Table (dashboard-only, with granularity) ─ */}
      <div className="pac-glass-raised rounded-lg border border-white/[0.06] overflow-hidden">
        <div className="pac-glass-subtle flex items-center justify-between px-4 py-2 border-b border-white/[0.04]">
          <div className="flex items-center gap-2">
            <span className="px-1.5 py-0.5 rounded bg-violet-600/20 text-violet-400 text-[10px] font-mono uppercase">
              archive summary
            </span>
            <span className="text-[10px] text-neutral-500">
              {summaryRows.length} {granularity} record{summaryRows.length !== 1 ? 's' : ''}
            </span>
          </div>
          <GranularityTabs active={granularity} onChange={setGranularity} />
        </div>
        <div className="max-h-[400px] overflow-auto">
          {summaryLoading ? (
            <div className="p-4 text-xs text-neutral-500 animate-pulse">Loading summary…</div>
          ) : (
            <DarkDataTable
              columns={summaryColumns}
              rows={summaryRows}
              getRowKey={(r, i) => `${r.scope_ns}-${r.period_start}-${i}`}
              frame={false}
              emptyMessage="No archive data yet. Trigger an archive run to populate."
            />
          )}
        </div>
      </div>
    </div>
  );
}
